import { FC, PropsWithChildren, useMemo } from 'react';
import { AnalyticsPageTracker, AnalyticsProvider, useDebugAnalyticsTrackerService } from '@csp/csp-web-analytics';
import { getUnifyVersion } from '@csp/csp-common-unify-version';
import { RoleType } from '@csp/dmdp-api-common-dto';
import { OrgId } from '@csp/csp-common-model';
import { ConsentType } from '@csp/dmdp-api-user-dto';
import { useStudyContext } from '../../../common/context/study/useStudyContext';
import { useCurrentUser } from '../../security/hooks/useCurrentUser';
import { CspPath } from '../../../CspPath';
import { useConsentContext } from '../../consent/context/hook/useConsentContext';

type Props = PropsWithChildren;
export const UnifyAnalyticsProvider: FC<Props> = ({ children }) => {
  const unifyVersion = getUnifyVersion();
  const { study } = useStudyContext();
  const studyCode = study.dcode;
  const { userId, displayIdpNames: userIdp, orgIdsByRole } = useCurrentUser();
  const { hasApprovedConsents } = useConsentContext();

  const orgIdsByRoleData = useMemo(
    () =>
      (Object.entries(orgIdsByRole) as [RoleType, OrgId[]][]).reduce<{ [Role in RoleType]?: string }>(
        (orgsByRole, [role, orgs]) => {
          orgsByRole[role] = orgs.join(',');
          return orgsByRole;
        },
        {},
      ),
    [orgIdsByRole],
  );

  const identifyProperties = useMemo(
    () => ({
      unifyVersion,
      studyCode,
      userIdp,
      ...orgIdsByRoleData,
    }),
    [unifyVersion, studyCode, userIdp, orgIdsByRoleData],
  );

  const { isEnabled: isDebugAnalyticsTrackerEnabled } = useDebugAnalyticsTrackerService();
  const shouldCollectWebAnalytics = useMemo(() => {
    const isWebAnalyticsEnabled = !!study.features?.summary.webAnalytics.isEnabledOnAnySite;
    const hasConsentedToAnalytics = hasApprovedConsents([ConsentType.ANALYTICS]);

    return isDebugAnalyticsTrackerEnabled || (isWebAnalyticsEnabled && hasConsentedToAnalytics);
  }, [hasApprovedConsents, study.features?.summary.webAnalytics.isEnabledOnAnySite, isDebugAnalyticsTrackerEnabled]);

  return (
    <AnalyticsProvider isEnabled={shouldCollectWebAnalytics} identifyProperties={identifyProperties} userId={userId}>
      <AnalyticsPageTracker routesMap={CspPath} />
      {children}
    </AnalyticsProvider>
  );
};
